import { create } from 'zustand';
import { companySocialIcons } from '../helpers/icon-maps';
import { companyServicesKey, defaultLangKey } from '../helpers/local-storage';
import {
  CompanyProfileType,
  CompanyType,
  fetchCompany,
  fetchCompanyProfile,
  putCompanyProfile,
} from './api/company-api';
import { useAlertStore } from './use-alert-store';

const validatedColor = (color?: string) => {
  if (!color) return undefined;
  const splitted = color.split(',').map((item) => item.trim().length > 0);
  const isAllMoreThanZero = splitted.every((item) => item);
  const isAllEmpty = splitted.every((item) => !item);
  if (isAllMoreThanZero || isAllEmpty) return color;
  return undefined;
};

interface Store {
  companyProfile?: Partial<CompanyProfileType>;
  saveCompanyProfile?: CompanyProfileType;
  company?: CompanyType;
  fetchCompanyProfile: () => Promise<void>;
  fetchCompany: () => Promise<void>;
  handleUpdateCompanyProfile: (data: Partial<CompanyProfileType>) => void;
  handleSaveCompanyProfile: () => Promise<void>;
  handleResetCompanyProfile: () => void;
  isLoading?: boolean;
}

export const useCompanyStore = create<Store>((set, get) => ({
  fetchCompany: async () => {
    set({ isLoading: true });
    try {
      const resp = await fetchCompany();

      localStorage.setItem(defaultLangKey, resp.default_lang);
      localStorage.setItem(companyServicesKey, resp.services.join(','));
      set({ company: resp });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по компании', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  fetchCompanyProfile: async () => {
    set({ isLoading: true });
    try {
      const resp = await fetchCompanyProfile();
      const socials = resp.socials || [];
      Object.keys(companySocialIcons).forEach((key) => {
        socials.some((el) => el.type === key) || socials.push({ type: key, url: '' });
      });
      const withDefaultSocials = { ...resp, socials };
      set({ saveCompanyProfile: withDefaultSocials });
      set({ companyProfile: withDefaultSocials });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по профилю компании', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  handleUpdateCompanyProfile: (data) => {
    set((store) => ({ ...store, companyProfile: { ...store.companyProfile, ...data } }));
  },
  handleResetCompanyProfile: () => {
    set((store) => ({ ...store, companyProfile: store.saveCompanyProfile }));
  },
  handleSaveCompanyProfile: async () => {
    set({ isLoading: true });
    try {
      const data = get().companyProfile;
      if (!data) {
        return;
      }
      const validatedData = {
        ...data.app,
        background: validatedColor(data.app?.background),
        colors: {
          primary: {
            elements: validatedColor(data.app?.colors?.primary?.elements),
            text: validatedColor(data.app?.colors?.primary?.text),
          },
          secondary: {
            elements: validatedColor(data.app?.colors?.secondary?.elements),
            text: validatedColor(data.app?.colors?.secondary?.text),
          },
          icon: {
            bg: data.app?.colors?.icon?.bg?.startsWith('http')
              ? data.app?.colors?.icon?.bg
              : validatedColor(data.app?.colors?.icon?.bg),
            stroke: validatedColor(data.app?.colors?.icon?.stroke),
          },
        },
      };
      const resp = await putCompanyProfile({ ...data, app: validatedData });
      useAlertStore
        .getState()
        .add({ label: 'Профиль компании успешно обновлен', variant: 'success' });
      set({ saveCompanyProfile: resp });
      set({ companyProfile: resp });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось обновить данные по профилю компании', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
}));
